import EditIcon from "@mui/icons-material/Edit";
import UndoIcon from "@mui/icons-material/Undo";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ExportButton from "Common/Components/Buttons/ExportButton";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import NameWithDate from "Common/Components/NameWithDate.tsx/NameWithDate";
import SearchBox from "Common/Components/SearchBox/SearchBox";
import StatusBadge from "Common/Components/StatusBadge/StatusBadge";
import MasterTableContainer from "Common/Tables/MasterFiltersTable/MasterTableContainer";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import LoadingSpinner from "components/LoadingSpinner";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { app_modules } from "helpers/apps_modules_list";
import { showConfirm, showError, showSuccess } from "helpers/swalHelper";
import usePermission from "hooks/usePermission";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { createSelector } from "reselect";
import {
  exportPackingyCSV,
  fetchWarehousePackingDone,
  handleRevertPackingDone,
} from "slices/Warehouse/Packing/PackingDone/thunk";
import { resetPackingDoneState } from "slices/Warehouse/Packing/PackingDone/reducer";
import { SweetAlertResult } from "sweetalert2";
import Tooltip from "@mui/material/Tooltip";
import StatusChip from "pages/Enquiry/Components/StatusChip";
import Chip from "components/Chips/Chip";
import { hideTagsSearchListingSection } from "../../utils/tagsSearchEmpty";

interface PackingDoneProps {
  warehouseId: any;
  noWrapper?: boolean;
}

const PackingDone = ({ warehouseId, noWrapper = false }: PackingDoneProps) => {
  const dispatch = useDispatch<any>();
  const selectItem = createSelector(
    (state: any) => state.WarehousePackingDoneReducer,
    (item) => ({
      list: item?.list,
      rowCount: item?.rowCount,
      loading: item?.loading,
      error: item?.error,
    }),
  );

  const { list, rowCount, loading, error } = useSelector(selectItem);

  const { canView, canUpdate } = usePermission(
    app_modules?.warehouse?.app_name,
    app_modules?.warehouse?.modules?.packing,
  );

  const tableState = usePreservedTableState({
    key: TableKeys.WAREHOUSE.PACKING.PACKING_DONE,
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue: colValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue: setColValue,
    resetState,
  } = tableState;

  // const [pageIndex, setPageIndex] = useState(0);
  // const [pageSize, setPageSize] = useState(10);
  // const [sorting, setSorting] = useState<SortingState>([]);
  // const [searchInput, setSearchInput] = useState("");
  // const [search, setSearch] = useState("");
  // const [columnFilters, setColumnFilters] = useState<any>();
  const navigate = useNavigate();
  const { tags: tagsParam } = useParams<{ tags?: string }>();
  const tagFromUrl = useMemo(() => {
    if (!tagsParam) return "";
    try {
      return decodeURIComponent(tagsParam).trim();
    } catch {
      return tagsParam.trim();
    }
  }, [tagsParam]);
  const isTagSearch = Boolean(tagFromUrl);
  const [data, setData] = useState<any[]>([]);
  const [listType, setListType] = useState<string>(
    sessionStorage?.getItem("warehousePackingDone") || "Transfer",
  );
  // const [colValue, setColValue] = useState<any>();
  const [tableInstance, setTableInstance] = useState<any>();
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";
  const effectiveSearch = tagFromUrl || search;

  const fetchPackingDone = async () => {
    await dispatch(
      fetchWarehousePackingDone({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search: effectiveSearch,
        ...(tagFromUrl ? { search_type: "tag" } : {}),
        filters: colValue,
        type: listType,
        ...(!isTagSearch ? { warehouse_id: warehouseId } : {}),
      }),
    );
  };

  useEffect(() => {
    if (isTagSearch || warehouseId) {
      fetchPackingDone();
    } else {
      dispatch(resetPackingDoneState());
    }
  }, [
    dispatch,
    pageIndex,
    colValue,
    pageSize,
    sorting,
    effectiveSearch,
    listType,
    warehouseId,
    isTagSearch,
  ]);

  // Handle search input with debounce
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  useEffect(() => {
    if (!tagFromUrl) return;
    setSearchInput(tagFromUrl);
    setSearch(tagFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagFromUrl]);

  const handleSearchChange = useCallback((e: any) => {
    setSearchInput(e.target.value);
  }, []);

  useEffect(() => {
    setData(Array.isArray(list) ? list : []);
  }, [list, rowCount]);

  const handleRevert = (packingId: string) => {
    showConfirm(
      "Are you sure you want to revert this packing? This action cannot be undone.",
    ).then((result: SweetAlertResult) => {
      if (result.isConfirmed) {
        dispatch(handleRevertPackingDone(packingId))
          .unwrap()
          .then(() => {
            showSuccess("Packing reverted successfully");
            fetchPackingDone();
          })
          .catch((error: any) => {
            console.error("Failed to revert packing:", error);
            showError(error?.message || error || "Failed to revert packing");
          });
      }
    });
  };
  // Filter menu items to check if any will be visible
  // This ensures we only show the Action column if there are visible menu items
  const DynamicAction = canView
    ? [
        {
          header: "Action",
          enableColumnFilter: false,
          enableSorting: false,
          cell: ({ row }: { row: any }) => {
            const data = row.original;
            const menuItems = [
              {
                icon: <VisibilityIcon fontSize="small" color="info" />,
                label: "View Details",
                permission: canView,
                onClick: (item: any) => {
                  navigate(
                    `/tags-search/warehouse-packing-done-view-details/${item?.packing_id}`,
                    { state: { listType } },
                  );
                },
                color: "primary",
              },
              ...(row.original?.dispatch_status.toLowerCase() ==
              "pending dispatch"
                ? [
                    {
                      icon: <EditIcon fontSize="small" color="primary" />,
                      label: "Edit",
                      permission: canUpdate,
                      onClick: (item: any) => {
                        navigate(
                          `/warehouse-packing-done-edit-details/${item?.packing_id}`,
                          { state: { listType } },
                        );
                      },
                      color: "primary",
                    },
                    {
                      label: "Revert",
                      icon: <UndoIcon fontSize="small" color="error" />,
                      permission: canUpdate,
                      onClick: () => handleRevert(row.original?.packing_id),
                    },
                  ]
                : []),
            ];
            return <DynamicMenuItem row={data} menuItems={menuItems} />;
          },
        },
      ]
    : [];

  const handleExport = useCallback(async () => {
    if (!warehouseId && !isTagSearch) {
      throw new Error("Please select a warehouse");
    }

    try {
      const result = await exportPackingyCSV({
        ...(!isTagSearch ? { warehouse_id: warehouseId } : {}),
        search: effectiveSearch,
        ...(isTagSearch ? { search_type: "tag" } : {}),
        filters: colValue,
      });

      return result;
    } catch (error: any) {
      console.error("Export error:", error);
      throw error;
    }
  }, [warehouseId, effectiveSearch, colValue, isTagSearch]);

  const baseURL = process.env.REACT_APP_WAREHOUSE_BACKEND_URL;

  const columns = useMemo(() => {
    const baseColumns = [
      {
        header: "Sr No.",
        accessorKey: "sr_no",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row, table }: { row: any; table: any }) => {
          const pageIndex = table.getState().pagination.pageIndex;
          const pageSize = table.getState().pagination.pageSize;
          const rowIndex = row.index;
          return pageIndex * pageSize + rowIndex + 1;
        },
      },
    ];

    const conditionalColumns = [
      {
        header: "Packing Number",
        accessorKey: "packing_number",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT.WAREHOUSE.PACKING_DONE.FILTER_DROPDOWN(
          "packing_number",
          warehouseId,
        )}`,
        cell: ({ row }: { row: any }) => {
          return row?.original?.packing_number || "-";
        },
      },
      {
        header: "SO Number",
        accessorKey: "sales_order_product__sales_order_number",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT.WAREHOUSE.PACKING_DONE.FILTER_DROPDOWN(
          "sales_order_number",
          warehouseId,
        )}`,
        cell: ({ row }: { row: any }) => {
          return row?.original?.sales_order_product?.sales_order_number || "-";
        },
      },
      {
        header: "Company Name",
        accessorKey: "sales_order_product__customer_name",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT.WAREHOUSE.PACKING_DONE.FILTER_DROPDOWN(
          "customer_name",
          warehouseId,
        )}`,
        cell: ({ row }: { row: any }) => {
          return row?.original?.sales_order_product?.customer_name || "-";
        },
      },
      {
        header: "Packing Date",
        accessorKey: "packing_date",
        enableColumnFilter: false,
        filterType: "date" as const,
        enableSorting: false,
        cell: ({ row }: { row: any }) => (
          <FormattedDate date={row.original.packing_date} />
        ),
      },
      {
        header: "Dispatch Status",
        accessorKey: "dispatch_status",
        enableSorting: false,
        enableColumnFilter: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.WAREHOUSE?.PACKING_DONE?.FILTER_DROPDOWN(
          "dispatch_status",
          warehouseId,
        )}`,
        cell: ({ row }: { row: any }) => {
          const status = row.original.dispatch_status;
          return <StatusChip status={status} />;

          const getStatusColor = (status: string) => {
            switch (status?.toLowerCase()) {
              case "pending dispatch":
                return "#fde4b4";
              case "dispatched":
                return "#be83bb";
              case "delivered":
                return "#81df86";
              default:
                return "#ccc";
            }
          };

          const displayText = status
            ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
            : "pending";

          return (
            <StatusBadge
              status={displayText}
              bgColor={status ? getStatusColor(status) : "#FF6C6C"}
              color="#000000ff"
            />
          );
        },
      },
      {
        header: "Notes",
        accessorKey: "notes",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT.WAREHOUSE.PACKING_DONE.FILTER_DROPDOWN(
          "notes",
          warehouseId,
        )}`,

        cell: ({ row }: { row: any }) => {
          const notes = row.original?.notes;
          if (!notes) return "-";

          return (
            <Tooltip
              placement="top"
              title={notes}
              componentsProps={{
                tooltip: {
                  sx: {
                    whiteSpace: "pre-line",
                    maxWidth: 350,
                    fontSize: "13px",
                  },
                },
              }}
            >
              <div
                style={{
                  minWidth: "150px",
                  maxWidth: "250px",
                  display: "-webkit-box",
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                  cursor: "pointer",
                }}
              >
                {notes}
              </div>
            </Tooltip>
          );
        },
      },
      {
        header: "Tags",
        accessorKey: "tags",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const tags: string[] = row.original.tags || [];
          if (!tags.length) return "-";

          const maxVisible = 3;
          const visible = tags.slice(0, maxVisible);
          const remaining = tags.length - visible.length;
          const tooltipText = tags.join(", ");

          return (
            <Tooltip
              arrow
              placement="top"
              title={tooltipText}
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 300,
                    fontSize: "13px",
                    whiteSpace: "normal",
                  },
                },
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "4px",
                  minWidth: "150px",
                  cursor: "pointer",
                }}
              >
                {visible.map((tag: string) => (
                  <Chip key={tag}>{tag}</Chip>
                ))}
                {remaining > 0 && <Chip>+{remaining} more</Chip>}
              </div>
            </Tooltip>
          );
        },
      },
      {
        header: "Created By/Date",
        accessorKey: "created_at",
        filterType: "date" as const,
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }: { row: any }) => (
          <NameWithDate
            name={row.original.created_by_user.username}
            date={row.original.created_at}
          />
        ),
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        filterType: "date" as const,
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }: { row: any }) => (
          <NameWithDate
            name={row.original.updated_by_user.username}
            date={row.original.updated_at}
          />
        ),
      },
      ...DynamicAction,
    ];

    return [...conditionalColumns];
  }, [listType, navigate, canView, warehouseId]);

  if (
    hideTagsSearchListingSection(
      noWrapper,
      loading,
      rowCount ?? 0,
      (data as any[])?.length ?? 0,
      error,
    )
  ) {
    return null;
  }

  return (
    <Fragment>
      <>
   
        {loading ? (
          <LoadingSpinner />
        ) : error ? (
          <div className="text-center py-4 text-danger">
            <h5>Error: {error}</h5>
          </div>
        ) : (
          <MasterTableContainer
            columns={columns}
            data={Array.isArray(data) ? data : []}
            totalRecords={rowCount || 0}
            pageIndex={pageIndex}
            pageSize={pageSize}
            onPageChange={setPageIndex}
            onPageSizeChange={setPageSize}
            columnFilters={columnFilters}
            setColumnFilters={setColumnFilters}
            colValue={setColValue}
            sorting={sorting}
            onSortingChange={setSorting}
            manualSorting={true}
            isBordered={false}
            isPagination={true}
            isGlobalFilter={false}
            SearchPlaceholder="Search ..."
            onTableReady={setTableInstance}
          />
        )}
      </>
    </Fragment>
  );
};

export default PackingDone;
